import { forwardRef, useEffect, useRef, useState } from 'react';
import { urlAbsoluteToRelative3catinfo } from '@portal-internet/core';
import { useEleccions } from 'hooks/3catinfo/useEleccions';
import { useEmitter } from 'hooks/useEmitter';
import TabSelector from 'components/3catinfo/Tabs/TabSelector';
import Boto from 'components/3catinfo/Boto';
import Enllac from 'components/3catinfo/Enllac';
import Titol from 'components/3catinfo/Titol';
import Dropdown from 'components/3catinfo/Dropdown';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import styles from './llistatResultats.module.scss';

const ItemLlistat = forwardRef((props, publicRef) => {
    const { id, nom, visible, routePath, url, slug } = props;
    const { getSlugEleccions } = useEleccions();
    const stylesItem = clsx({
        [styles.contentItem]: true,
        [styles.ocult]: !visible
    });
    const route = {
        type: routePath,
        slug: slug || getSlugEleccions(),
        slug2: nom,
        id: id,
        href: url
            ? urlAbsoluteToRelative3catinfo(url.replace('/324/', '/3catinfo/').replace('/3catinfo/eleccions/', '/3catinfo/'))
            : ''
    };

    return (
        <li className={stylesItem}>
            <Enllac
                route={route}
                className={styles.item}
                title={`Resultat eleccions a ${nom}`}
                tabIndex={visible ? 0 : -1}
                ref={publicRef}
            >
                {nom}
            </Enllac>
        </li>
    );
});

const LlistatResultatsItems = ({ items = [], paginacio, routePath }) => {
    const itemsPage = 32;
    const totalItems = items.length;
    const totalPages = Math.ceil(totalItems / itemsPage);

    const [currentPage, setCurrentPage] = useState(0);
    const [announcement, setAnnouncement] = useState('');
    const [toggleMessage, setToggleMessage] = useState(true);

    const newItemRef = useRef(null);

    const handleNext = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage((prev) => prev + 1);

            setAnnouncement(toggleMessage ? "S'ha mostrat més contingut" : "S'ha carregat més contingut");
            setToggleMessage(!toggleMessage);

            setTimeout(() => setAnnouncement(''), 100);
        }
    };

    useEffect(() => {
        if (currentPage > 0 && newItemRef.current) {
            newItemRef.current.focus();
        }
    }, [currentPage]);

    if (__isEmpty(items)) return null;

    return (
        <>
            <ul className={styles.listItems}>
                {items.map((item, index) => (
                    <ItemLlistat
                        {...item}
                        key={`item-resultat-${item.id || item.slug}`}
                        visible={Math.floor(index / itemsPage) <= currentPage || !paginacio}
                        ref={index === currentPage * itemsPage ? newItemRef : null}
                        routePath={routePath}
                    />
                ))}
            </ul>

            {currentPage < totalPages - 1 && paginacio && (
                <div className={styles.contentButtonCarregaMes}>
                    <Boto
                        tag='button'
                        text='Mostra’n més'
                        icona='fletxa_simple_avall'
                        estil='transparent'
                        mida='s'
                        className={styles.carregaMes}
                        onClick={handleNext}
                    />
                </div>
            )}

            <div aria-live='assertive' className={styles.announcement}>
                {announcement}
            </div>
        </>
    );
};

const BlocLlistat = (props) => {
    const { item, selected, paginacio } = props;

    return (
        <div
            className={clsx(styles.bloc, {
                [styles.ocult]: selected?.items !== item.items
            })}
        >
            <div className={styles.blocResultats}>
                <LlistatResultatsItems paginacio={paginacio} items={props[item.items]} routePath={item.path} />
            </div>
        </div>
    );
};

const LlistatResultats = (props) => {
    const { id, titol, tag, items = [] } = props;
    const { onEmitter } = useEmitter();
    const [selectedBloc, setSelectedBloc] = useState(0);
    const [selected, setSelected] = useState(items?.[0]?.tabsData?.[0]?.valor ?? items?.[0]?.valor);

    const updateDropdown = (item, index) => {
        const value = item.valor ?? items?.[index]?.tabsData?.[index]?.valor;
        setSelected(value);
        setSelectedBloc(index);
    };

    onEmitter(id, (value) => {
        setSelected(value);
    });

    const showDropdown = items.length > 1;
    const tabsData = items[selectedBloc]?.tabsData;

    return (
        <div className={styles.contentLlistatResultats}>
            <div className={styles.capcalera}>
                <div className={styles.contentTitol}>
                    <Titol tag={tag || 'h2'} titol={titol} estil='ambParagraf' />
                </div>

                {(showDropdown || tabsData) && (
                    <div className={styles.controls}>
                        {showDropdown && (
                            <Dropdown
                                estil='selectorResultats'
                                items={items}
                                updateDropdown={updateDropdown}
                                isBlancIcona={false}
                            />
                        )}
                        {tabsData && (
                            <TabSelector
                                tabId={id}
                                items={tabsData}
                                estils='pb-0'
                                estilBotons='petit'
                                className={styles.tabSelector}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className={styles.contentResultats}>
                {items?.map((itemConfig) => {
                    const paginacio = itemConfig.paginacio;
                    if (itemConfig.tabsData) {
                        return itemConfig.tabsData?.map((itemTab) => (
                            <BlocLlistat
                                key={`llistat-resultats-items-${itemTab.valor.items}`}
                                item={itemTab.valor}
                                selected={selected}
                                paginacio={paginacio}
                                {...props}
                            />
                        ));
                    } else {
                        return (
                            <BlocLlistat
                                key={`llistat-resultats-items-${itemConfig.valor.items}`}
                                item={itemConfig.valor}
                                selected={selected}
                                paginacio={paginacio}
                                {...props}
                            />
                        );
                    }
                })}
            </div>
        </div>
    );
};

export default LlistatResultats;

ItemLlistat.displayName = 'ItemLlistat';
LlistatResultatsItems.displayName = 'LlistatResultatsItems';
